import {
	browserProfilingIntegration,
	type ErrorEvent,
	init,
	reactRouterTracingIntegration,
	replayIntegration,
	zodErrorsIntegration,
} from '@sentry/react-router'

// Спільна ініціалізація Sentry для кабінетів (citynet + langate). dsn — єдина бренд-відмінність.
// Викликати лише в проді (import.meta.env.PROD && MODE === 'production') з entry.client-а.

// Реклама/аналітика Google — шум third-party скриптів (не наш код), глушимо breadcrumb, event і denyUrls.
const GA_PATTERNS = [
	/google-analytics\.com/i,
	/analytics\.google\.com/i,
	/googletagmanager\.com/i,
	/\/g\/collect/i,
	/googleadservices\.com/i,
	/googlesyndication\.com/i,
	/doubleclick\.net/i,
]

// Мережевий збій fetch (обрив, CORS, недоступний сервер) — транзієнтний, неактионебельний.
// Формулювання різниться між браузерами: Chrome «Failed to fetch», Firefox «NetworkError
// when attempting to fetch resource», Safari «Load failed». Глушимо всі три.
const networkError = /failed to fetch|networkerror|load failed/i

const isGA = (value?: unknown): boolean => typeof value === 'string' && GA_PATTERNS.some((pattern) => pattern.test(value))

// Скрипт, інжектований розширенням браузера, не має URL — кожен його кадр приходить як
// `<anonymous>` (напр. рекурсивна підміна Object.getOwnPropertyDescriptor → «Maximum call
// stack size exceeded», CITYNET-CABINET-UI-7/8). Наш бандл завжди має URL файлу. Вбудований
// inbound-фільтр Sentry «browser extensions» такого не ловить — він матчить chrome-extension://.
// Перевіряємо кожне значення ланцюга: React загортає чужу помилку в свою (Error 520 з cause).
export const isInjectedScriptEvent = (event: ErrorEvent): boolean =>
	event.exception?.values?.some(({ stacktrace }) => {
		const frames = stacktrace?.frames ?? []
		return frames.length > 0 && frames.every(({ filename }) => filename === '<anonymous>')
	}) ?? false

const initSentry = (dsn: string): void => {
	init({
		attachStacktrace: true,
		beforeBreadcrumb(breadcrumb) {
			return breadcrumb.type === 'http' && isGA(breadcrumb.data?.url) ? null : breadcrumb
		},
		beforeSend(event, hint) {
			if (isGA(event.request?.url) || isGA(event.message) || isInjectedScriptEvent(event)) return null
			const error = hint.originalException
			if (error instanceof Error && (networkError.test(error.message) || isGA(error.message))) return null
			return event.exception?.values?.[0]?.value && isGA(event.exception.values[0].value) ? null : event
		},
		// eslint-disable-next-line @typescript-eslint/no-deprecated -- діє, бо traceLifecycle: 'static' нижче; зникає у v12
		beforeSendTransaction: (event) => (event.tags?.ignore ? null : event),
		denyUrls: GA_PATTERNS,
		dsn,
		ignoreErrors: [networkError],
		integrations: [
			reactRouterTracingIntegration(),
			browserProfilingIntegration(),
			replayIntegration({
				maskAllInputs: false,
				maskAllText: false,
				networkDetailAllowUrls: [location.origin],
				// Тіло автентифікації в replay НЕ капчимо: networkCaptureBodies дефолтно true,
				// тож same-origin POST /api/v1/core/login інакше лягав би в реплей із plaintext
				// паролем (deny має пріоритет над allow). Дзеркало mfe-lib/init-sentry.
				networkDetailDenyUrls: [/\/api\/v1\/core\/login/],
				networkRequestHeaders: ['Cache-Control'],
				networkResponseHeaders: ['Referrer-Policy'],
			}),
			zodErrorsIntegration(),
		],
		profileLifecycle: 'trace',
		profileSessionSampleRate: 0.2,
		replaysOnErrorSampleRate: 1,
		replaysSessionSampleRate: 0,
		// SDK v11 за замовчуванням стрімить спани ('stream') і тоді НЕ викликає beforeSendTransaction
		// (фільтр tags.ignore вище). 'static' повертає транзакції й хук.
		traceLifecycle: 'static',
		tracesSampleRate: 1,
		tunnel: '/api/monitoring',
	})
}

export default initSentry
